/**
 * Combined search filter bar (F2): text + mood + tag +
 * date range. Controlled by the parent via signals; no state of its own.
 */
import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Search as SearchIcon, X } from "@fleet/icons";
import { cn, MOOD_EMOJIS, MOOD_VALUES, type MoodValue } from "@journal/shared";
import type { JournalTag } from "@journal/powersync";
import { useI18n } from "@/providers/i18n-provider";

export interface EntryFilterState {
  term: string;
  mood: MoodValue | null;
  tagId: string | null;
  startKey: string;
  endKey: string;
}

interface EntryFiltersProps {
  state: () => EntryFilterState;
  onChange: (patch: Partial<EntryFilterState>) => void;
  onClear: () => void;
  tags: () => JournalTag[];
  hasActiveFilters: () => boolean;
}

export function EntryFilters(props: EntryFiltersProps) {
  const { t } = useI18n();

  return (
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-2 rounded-2xl border border-border/50 bg-card/70 px-3.5 py-2.5">
        <SearchIcon class="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          type="text"
          data-testid="search-input"
          class="flex-1 bg-transparent text-[15px] text-foreground outline-none placeholder:text-muted-foreground/50"
          placeholder={t(msg`Search words, places, people…`)}
          value={props.state().term}
          onInput={(e) => {
            props.onChange({ term: e.currentTarget.value });
          }}
          autofocus
        />
      </div>

      <div class="flex flex-wrap items-center gap-2" data-testid="filter-bar">
        {/* Mood */}
        <div
          class="flex items-center gap-1"
          role="radiogroup"
          aria-label={t(msg`How do you feel?`)}
        >
          <For each={MOOD_VALUES}>
            {(value) => (
              <button
                type="button"
                role="radio"
                aria-checked={props.state().mood === value}
                data-testid={`filter-mood-${value}`}
                title={`${value}/5`}
                class={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-transparent bg-muted/40 text-base transition-colors hover:bg-muted",
                  props.state().mood === value && "border-primary/40 bg-primary/15",
                )}
                onClick={() => {
                  props.onChange({ mood: props.state().mood === value ? null : value });
                }}
              >
                {MOOD_EMOJIS[value]}
              </button>
            )}
          </For>
        </div>

        {/* Rango de fechas */}
        <label class="flex items-center gap-1 text-[11px] text-muted-foreground/70">
          {t(msg`From`)}
          <input
            type="date"
            data-testid="filter-date-from"
            class="rounded-lg border border-border/50 bg-muted/30 px-2 py-1 text-xs text-foreground outline-none focus:border-primary/40"
            value={props.state().startKey}
            onInput={(e) => {
              props.onChange({ startKey: e.currentTarget.value });
            }}
          />
        </label>
        <label class="flex items-center gap-1 text-[11px] text-muted-foreground/70">
          {t(msg`To`)}
          <input
            type="date"
            data-testid="filter-date-to"
            class="rounded-lg border border-border/50 bg-muted/30 px-2 py-1 text-xs text-foreground outline-none focus:border-primary/40"
            value={props.state().endKey}
            onInput={(e) => {
              props.onChange({ endKey: e.currentTarget.value });
            }}
          />
        </label>

        <Show when={props.hasActiveFilters()}>
          <button
            type="button"
            data-testid="filter-clear"
            class="flex items-center gap-1 rounded-full border border-border/50 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => {
              props.onClear();
            }}
          >
            <X class="h-3 w-3" />
            {t(msg`Clear filters`)}
          </button>
        </Show>
      </div>

      {/* Etiquetas */}
      <Show when={props.tags().length > 0}>
        <div class="flex flex-wrap items-center gap-1.5" data-testid="filter-tags">
          <For each={props.tags()}>
            {(tag) => (
              <button
                type="button"
                data-testid={`filter-tag-${tag.name ?? ""}`}
                class={cn(
                  "rounded-full border border-transparent bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                  props.state().tagId === tag.id && "border-primary/30 bg-primary/12 text-primary",
                )}
                onClick={() => {
                  props.onChange({ tagId: props.state().tagId === tag.id ? null : tag.id });
                }}
              >
                #{tag.name}
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
